import { z } from 'zod';
/** Review demo-data slice — the Trade Replay + backtest entities the reference `review()` method embeds
 *  (design-reference script lines 902–907 BT_BOOKS, 3106–3541 review()). Every field is deterministic, seeded and labeled simulated.
 *  REV_DAYS / REV_ACCTS (the reviewed sessions and account ledger) live in the core slice and are shared through `trades()`. */

/** Playbook stats the backtest solves against — the same books Session Setup arms. */
export const BtBookSchema = z
  .object({
    wr: z.number(), // win rate
    avgWin: z.number(), // average winner in R
    avgLoss: z.number(), // average loser in R
    perDay: z.number(), // expected entries per session
    window: z.string(), // declared session window
  })
  .strict();
/** One AI grade variant: the score shown and the short tag next to it. */
export const ReplayGradeSchema = z.object({ score: z.string(), tag: z.string() }).strict();
/** The replay AI grading rows — entry/exit/plan — with their variant texts; the slice picks the variant from the trade's R, P&L and book. */
export const ReplayGradingSchema = z
  .object({
    entry: z
      .object({
        label: z.string(),
        precise: ReplayGradeSchema,
        acceptable: ReplayGradeSchema,
        poor: ReplayGradeSchema,
        narrGood: z.string(),
        narrBad: z.string(),
      })
      .strict(),
    exit: z
      .object({
        label: z.string(),
        held: ReplayGradeSchema,
        stopped: ReplayGradeSchema,
        narrGood: z.string(),
        narrBad: z.string(),
      })
      .strict(),
    plan: z
      .object({
        label: z.string(),
        on: ReplayGradeSchema,
        off: ReplayGradeSchema,
        narrGood: z.string(),
        narrBad: z.string(),
      })
      .strict(),
  })
  .strict();
/** Replay session meta — the reconstructed H1 session length and the account/risk the session table shows. */
export const ReplaySessionSchema = z
  .object({
    totalBars: z.number(),
    account: z.string(),
    riskPerTrade: z.string(),
  })
  .strict();
export const ReviewDataSchema = z
  .object({
    btBooks: z.record(z.string(), BtBookSchema),
    /** Null when no replay has been prepared — an ordinary state, and the state every new account is in. The
     *  screen renders its empty card rather than the whole product rendering an error page. */
    replaySession: ReplaySessionSchema.nullable(),
    /** Null with the session. The grading rubric is vocabulary rather than the trader's data, but it arrives with
     *  the bundle, so its absence is the bundle's absence. */
    replayGrading: ReplayGradingSchema.nullable(),
  })
  .strict();
export type ReviewData = z.infer<typeof ReviewDataSchema>;
export type BtBook = z.infer<typeof BtBookSchema>;
export type ReplayGrade = z.infer<typeof ReplayGradeSchema>;
export type ReplayGrading = z.infer<typeof ReplayGradingSchema>;
export type ReplaySession = z.infer<typeof ReplaySessionSchema>;
